import { Component, EventEmitter, inject, Input, OnDestroy, Output, ViewEncapsulation } from '@angular/core';
import { NgxExtendedPdfViewerService, PdfBreakpoints, PDFPrintRange, PdfSidebarView } from 'ngx-extended-pdf-viewer';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DialogComponent } from '../dialog/dialog.component';
import { getErrorMessage, parsePageSelection } from '@app/commons/utils/utils';
import { SnackbarService } from '@app/services/snackbar.service';
import * as PdfUtils from '@app/commons/utils/pdf-utils';
import { ViewConfig } from '@app/commons/utils/view-config';
import { IDocumentPrint } from '@app/commons/types/interfaces/documents.interface';

@Component({
  selector: 'app-pdf-viewer',
  templateUrl: './pdf-viewer.component.html',
  styleUrl: './pdf-viewer.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class PdfViewerComponent implements OnDestroy {
  @Input() src: string | Uint8Array | URL | ArrayBuffer | Blob = '';
  @Input() fullScreen = false;
  @Input() fileName: string | undefined | null = 'Document';
  @Input() page = 1;
  @Output() load: EventEmitter<any> = new EventEmitter();
  @Output() expand: EventEmitter<any> = new EventEmitter();
  @Output() close: EventEmitter<any> = new EventEmitter();
  @Output() export: EventEmitter<any> = new EventEmitter();
  modalService = inject(NgbModal);
  originalSrc = '';
  originalSrcArrayBuffer: ArrayBuffer | undefined;
  totalPages = 0;
  
  spreadMode: 'off' | 'even' | 'odd' = 'off';
  zoom = 'auto';
  maxZoom = 1.25;
  zoomLevels = ['auto', 'page-fit', 0.5, 0.75, 1];
  activeSidebarView: PdfSidebarView = PdfSidebarView.ATTACHMENTS;
  searchValue = '';
  selectedPages: IDocumentPrint[] = [];
  viewConfig = new ViewConfig();
  fileNameWithoutExtension: string | null | undefined;
  fileExtension: string | null | undefined;

  private findbarObserver: MutationObserver | null = null;
  private findbarTrapSetupAttempts = 0;
  private findbarTrapListenersActive = false;
  private viewFindKeydownCleanup: (() => void) | null = null;
  private viewFindKeydownButton: HTMLButtonElement | null = null;
  private viewFindNativeSetupAttempts = 0;
  private readonly findbarTrapKeydownBound = (e: KeyboardEvent) =>
    this.onFindbarDocumentTrapKeydown(e);
  private readonly findbarTrapFocusInBound = (e: FocusEvent) =>
    this.onFindbarDocumentTrapFocusIn(e);
  private readonly viewFindKeydownNativeBound = (e: Event) =>
    this.onViewFindActivate(e);

  constructor(
    private pdfViewerService: NgxExtendedPdfViewerService,
    private snackBarService: SnackbarService
  ) {
    // setting breakpoints lowest so that buttons not get hide
    PdfBreakpoints.xs = 50; // unit: pixels
    PdfBreakpoints.sm = 50;
    PdfBreakpoints.md = 50;
    PdfBreakpoints.lg = 50;
    PdfBreakpoints.xl = 50;
    PdfBreakpoints.xxl = 50;
    this.viewConfig.setLoading(true);
  }

  async onPagesFailed($event: any) {
    this.viewConfig.setLoading(false);
    this.snackBarService.show(getErrorMessage($event));
    this.load.emit();
  }

  async onPagesLoaded() {
    this.load.emit();
    // the original url we pass is string, once pdf is shorten by filter, it becomes unitarray and pagesLoad function is called again
    // so to keep original src as intial, we will not change it if its not string
    if (typeof this.src === 'string') {
      this.originalSrc = this.src as string; // keeping src in another variable as it will be used in later
      // we will also keep the pdf as UnitArray instead of downloading from url each time
      this.originalSrcArrayBuffer = await PdfUtils.loadFromUrl(this.originalSrc);
      this.totalPages = this.pdfViewerService.numberOfPages();
    } else {
      // here means the pages are filtered by user and these are typically less pages as user will export these
      // we will select the checkboxes so that user can export
      const totalPagesCount = this.pdfViewerService.numberOfPages();
      // index starts with 1 because id of pages start with 1
      for (let index = 1; index <= totalPagesCount; index++) {
        let page = index.toString();
        const element = document.getElementById(page) as HTMLInputElement;
        this.saveChecboxState(element, page, true); // marking true for all pages
      }
    }
    this.viewConfig.setLoading(false);
    this.updateFileNameMeta();
    this.shouldSelectMainCheckbox();
    queueMicrotask(() => {
      this.ensureFindbarFocusTrap();
      this.ensureViewFindNativeKeydown();
    });
  }

  ngOnDestroy(): void {
    this.teardownFindbarFocusTrap();
    this.detachViewFindNativeKeydown();
  }

  /**
   * PDF.js registers a window-level keydown handler that treats Space/Enter like page-down.
   * Stop propagation in capture phase so the native button / select behavior runs.
   */
  onViewFindActivate(ev: Event): void {
    if (!(ev instanceof KeyboardEvent)) {
      return;
    }
    if (ev.key !== 'Enter' && ev.key !== ' ' && ev.key !== 'Spacebar') {
      return;
    }
    ev.preventDefault();
    ev.stopPropagation();
    (ev.currentTarget as HTMLButtonElement).click();
  }

  onZoomSelectKeydown(ev: Event): void {
    if (!(ev instanceof KeyboardEvent)) {
      return;
    }
    if (ev.key !== 'Enter' && ev.key !== ' ' && ev.key !== 'Spacebar') {
      return;
    }
    ev.stopPropagation();
    const sel = ev.currentTarget as HTMLSelectElement & { showPicker?: () => void };
    if (ev.key === 'Enter' || ev.key === ' ' || ev.key === 'Spacebar') {
      if (typeof sel.showPicker === 'function') {
        ev.preventDefault();
        try {
          sel.showPicker();
        } catch {
          sel.focus();
        }
      }
    }
  }

  private static readonly maxFindbarSetupAttempts = 30;

  private ensureFindbarFocusTrap(): void {
    if (this.findbarObserver) {
      return;
    }
    const findbar = document.getElementById('findbar');
    if (!findbar) {
      if (this.findbarTrapSetupAttempts++ < PdfViewerComponent.maxFindbarSetupAttempts) {
        setTimeout(() => this.ensureFindbarFocusTrap(), 200);
      }
      return;
    }
    this.findbarTrapSetupAttempts = 0;
    this.findbarObserver = new MutationObserver(() =>
      this.syncFindbarTrapListeners(findbar)
    );
    this.findbarObserver.observe(findbar, {
      attributes: true,
      attributeFilter: ['class'],
    });
    this.syncFindbarTrapListeners(findbar);
  }

  private syncFindbarTrapListeners(findbar: HTMLElement): void {
    const open = !findbar.classList.contains('hidden');
    if (open === this.findbarTrapListenersActive) {
      return;
    }
    this.findbarTrapListenersActive = open;
    if (open) {
      document.addEventListener('keydown', this.findbarTrapKeydownBound, true);
      document.addEventListener('focusin', this.findbarTrapFocusInBound, true);
    } else {
      document.removeEventListener('keydown', this.findbarTrapKeydownBound, true);
      document.removeEventListener('focusin', this.findbarTrapFocusInBound, true);
    }
  }

  private teardownFindbarFocusTrap(): void {
    if (this.findbarTrapListenersActive) {
      document.removeEventListener('keydown', this.findbarTrapKeydownBound, true);
      document.removeEventListener('focusin', this.findbarTrapFocusInBound, true);
      this.findbarTrapListenersActive = false;
    }
    this.findbarObserver?.disconnect();
    this.findbarObserver = null;
  }

  /**
   * Native capture listener so Enter/Space work even if PDF.js or projection
   * interferes with Angular's template binding on the toolbar.
   */
  private ensureViewFindNativeKeydown(): void {
    const btn = this.getFindToggleButton();
    if (!btn) {
      if (this.viewFindNativeSetupAttempts++ < PdfViewerComponent.maxFindbarSetupAttempts) {
        setTimeout(() => this.ensureViewFindNativeKeydown(), 200);
      }
      return;
    }
    this.viewFindNativeSetupAttempts = 0;
    if (this.viewFindKeydownButton === btn) {
      return;
    }
    this.detachViewFindNativeKeydown();
    btn.addEventListener('keydown', this.viewFindKeydownNativeBound, true);
    this.viewFindKeydownButton = btn;
    this.viewFindKeydownCleanup = () => {
      btn.removeEventListener('keydown', this.viewFindKeydownNativeBound, true);
      this.viewFindKeydownButton = null;
      this.viewFindKeydownCleanup = null;
    };
  }

  private detachViewFindNativeKeydown(): void {
    this.viewFindKeydownCleanup?.();
  }

  private getFocusableInFindbar(findbar: HTMLElement): HTMLElement[] {
    const sel =
      'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(findbar.querySelectorAll<HTMLElement>(sel)).filter(
      (el) => this.isFocusTrapElementVisible(el) && !el.closest('.hidden')
    );
  }

  /** Find-in-document toggle (PDF.js id); must stay in the tab ring while find UI is open. */
  private getFindToggleButton(): HTMLButtonElement | null {
    const el = document.getElementById('viewFind');
    return el instanceof HTMLButtonElement ? el : null;
  }

  /**
   * Tab order while find is open: all controls inside #findbar, then #viewFind (toggle / close).
   */
  private getFindbarTrapFocusables(findbar: HTMLElement): HTMLElement[] {
    const inside = this.getFocusableInFindbar(findbar);
    const toggle = this.getFindToggleButton();
    if (
      toggle &&
      this.isFocusTrapElementVisible(toggle) &&
      !toggle.classList.contains('invisible')
    ) {
      return [...inside, toggle];
    }
    return inside;
  }

  private isFocusTrapElementVisible(el: HTMLElement): boolean {
    if (!el.isConnected) {
      return false;
    }
    const st = window.getComputedStyle(el);
    if (st.display === 'none' || st.visibility === 'hidden') {
      return false;
    }
    return true;
  }

  private onFindbarDocumentTrapKeydown(ev: KeyboardEvent): void {
    if (ev.key === 'Escape') {
      const findbar = document.getElementById('findbar');
      if (!findbar || findbar.classList.contains('hidden')) {
        return;
      }
      // Defer so PDF.js can close the findbar on Escape first; then return focus to the toolbar toggle.
      setTimeout(() => {
        this.getFindToggleButton()?.focus();
      }, 0);
      return;
    }
    if (ev.key !== 'Tab') {
      return;
    }
    const findbar = document.getElementById('findbar');
    if (!findbar || findbar.classList.contains('hidden')) {
      return;
    }
    const focusables = this.getFindbarTrapFocusables(findbar);
    if (!focusables.length) {
      return;
    }
    const active = document.activeElement as HTMLElement | null;
    let i = active ? focusables.indexOf(active) : -1;
    if (i < 0) {
      return;
    }
    ev.preventDefault();
    const len = focusables.length;
    const next = ev.shiftKey
      ? focusables[(i - 1 + len) % len]
      : focusables[(i + 1) % len];
    next.focus();
  }

  private onFindbarDocumentTrapFocusIn(ev: FocusEvent): void {
    const findbar = document.getElementById('findbar');
    if (!findbar || findbar.classList.contains('hidden')) {
      return;
    }
    const target = ev.target as Node | null;
    if (!target) {
      return;
    }
    if (findbar.contains(target)) {
      return;
    }
    const toggle = this.getFindToggleButton();
    if (toggle && (toggle === target || toggle.contains(target))) {
      return;
    }
    const input = findbar.querySelector<HTMLElement>('#findInput');
    input?.focus();
  }

  onExpandClick() {
    this.expand.emit();
  }

  onSelectAll($event: Event) {
    const key = (<HTMLInputElement>$event.target).id;
    const selectAllElement = document.getElementById(key) as HTMLInputElement;
    
    const totalPagesCount = this.pdfViewerService.numberOfPages();
    // index starts with 1 because id of pages start with 1
    for (let index = 1; index <= totalPagesCount; index++) {
      let page = index.toString();
      const element = document.getElementById(page) as HTMLInputElement;
      this.saveChecboxState(element, page, selectAllElement.checked);
    }
  }

  updateFileNameMeta() {
    const match = this.fileName?.match(/(.*)\.([^.]+)$/);
    if (match) {
      this.fileNameWithoutExtension = match[1];
      this.fileExtension = match[2];
      return;
    }
    // If no extension is found, return the filename and null for the extension
    this.fileNameWithoutExtension = this.fileName;
    this.fileExtension = undefined;
  }

  shouldSelectMainCheckbox() {
    let allSelected = true; // Assume all checkboxes are selected by default

    const totalPagesCount = this.pdfViewerService.numberOfPages();
    for (let index = 1; index <= totalPagesCount; index++) {
      const page = index.toString();
      const element = document.getElementById(page) as HTMLInputElement;

      if (element && !element.checked) {
        allSelected = false; // If any checkbox is not selected, set to false
        break; // No need to check further
      }
    }

    // Set the main checkbox based on `allSelected`
    const mainCheckbox = document.getElementById('select-all') as HTMLInputElement;
    if (mainCheckbox) {
      mainCheckbox.checked = allSelected;
    }
  }

  onClick($event: Event) {
    const key = (<HTMLInputElement>$event.target).id;
    const element = document.getElementById(key) as HTMLInputElement;
    setTimeout(() => {
      this.saveChecboxState(element, key, !element.checked);
      this.shouldSelectMainCheckbox();
    }, 0);
  }

  saveChecboxState(element: HTMLInputElement, key: string, value: boolean) {
    // update checkbox
    element.checked = value;
    const isExists = this.selectedPages.find(page => page.pageIndex === Number(key));
    if (!isExists) {
      this.selectedPages.push({
        pageIndex: Number(key),
        pageNumber: Number(key),
        isChecked: false,
      });
    }
    for (let index = 0; index < this.selectedPages.length; index++) {
      const page = this.selectedPages[index];
      if (parseInt(key) === page.pageIndex) {
        page.isChecked = false;
        if (value) {
          page.isChecked = true;
        }
      }
    }
  }

  async resetPdfToOriginal($event: Event) {
    // pdf will be reset to its original state
    if (this.searchValue.trim() === '' && typeof this.src !== 'string') {
      this.src = this.originalSrc;
      this.viewConfig.setLoading(true);
      this.selectedPages = [];
    }
  }

  async onSearchPages() {
    if (this.viewConfig.loading) {
      this.snackBarService.show('Please wait while the document loads.');
      return;
    }
    // logic to calculate pages to print
    const pages = parsePageSelection(this.searchValue);

    if (typeof pages === 'string') {
      this.snackBarService.show(pages);
      return;
    }

    if (!pages.length) {
      this.snackBarService.show(`Please provide valid pages to export`);
      return;
    }

    this.viewConfig.setLoading(true);
    this.selectedPages = [];
    let isError = false;

    // validate the pages going to export does not exceed total page
    for (let index = 0; index < pages.length; index++) {
      const page = pages[index];
    
      if (page > this.totalPages) {
        this.snackBarService.show(`Please provide pages less than or equal to ${this.totalPages}`);
        this.viewConfig.setLoading(false);
        isError = true;
        break; // Exit the loop
      }
    
      this.selectedPages.push({
        pageIndex: index + 1, // mimic page numbers, starts from 1
        pageNumber: page,
        isChecked: false,
      });
    }

    // saving the extracted pdf in src variable as we will display those pages only
    // pdf will be created from originalSrcArrayBuffer only

    if (this.originalSrcArrayBuffer && !isError) {
      this.src = await PdfUtils.createPdfFromUrl(this.originalSrcArrayBuffer, pages);
      this.viewConfig.setLoading(false);
    }
  }

  getSelectedPages() {
    return this.selectedPages;
  }

  async exportPdf() {
    // it get alls the pages in filter list
    const allPages = this.getSelectedPages();
    
    // get only those which are selected
    const pagesToPrint: number[] = [];
    allPages.forEach(page =>  {
      if (page.isChecked) {
        pagesToPrint.push(page.pageNumber);
      }
    });

    if (!pagesToPrint.length) {
      this.snackBarService.show('Please provide pages to export');
      return;
    }

    const modalRef = this.modalService.open(DialogComponent, {
      backdrop: true,
    });
    modalRef.componentInstance.alert = {
      message: '',
      type: '',
    };
		modalRef.componentInstance.title = `Are you Sure?`;
    modalRef.componentInstance.message = `Are you sure you want to export the following pages?`;
    modalRef.componentInstance.buttons = [{
      id: 0,
      type: 'outline-dark',
      class: 'ind-button-md',
      label: 'No',
      onClick: () => {
        modalRef.close();
      }
    }, {
      id: 1,
      type: 'primary',
      class: 'ind-button-md',
      label: 'Yes',
      onClick: () => {
        const button = modalRef.componentInstance.buttons[1];
        button.disabled = true;
        button.label = 'Exporting...';
        this.onExportConfirm(pagesToPrint);
        setTimeout(() => {
          this.export.emit();
        }, 2000);
      }
    }];
  }
  
  async onExportConfirm(pagesToPrint: number[]) {
    if (this.originalSrcArrayBuffer) {
      const pdf = await PdfUtils.createPdfFromUrl(this.originalSrcArrayBuffer, pagesToPrint);
      const fileNames = [
        this.fileNameWithoutExtension,
      ];
      if (this.searchValue) {
        fileNames.push(this.searchValue);
      }
      fileNames.push(this.fileExtension);
      PdfUtils.downloadPdf(pdf, fileNames.join('.'));
    }
  }

  onClose() {
    this.close.emit();
  }
}
